import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FileSpreadsheet, Receipt, Landmark, Banknote, Calculator, ChevronDown, ChevronRight, ChevronLeft, CreditCard, Users, Gauge } from "lucide-react";
import { http } from "../lib/api";
import { safePath } from "../lib/nav";
import { useAuth } from "../context/AuthContext";
import { canSeeSub, canLimitRisk } from "../lib/roles";
import CreditCards from "./CreditCards";
import DataEntry from "./DataEntry";
import Invoices from "./Invoices";
import Checks from "./Checks";
import BankLoans from "./BankLoans";
import LimitRisk from "./LimitRisk";
import ProjectCostEntry from "./ProjectCostEntry";
import Personnel from "./Personnel";

const SECTIONS = [
  { key: "hizmet", label: "Hizmet Veri Girişi", desc: "Şube bazlı hizmet kayıtları ve toplu Excel girişi", icon: FileSpreadsheet, node: <DataEntry /> },
  { key: "personel", label: "Personel Girişi", desc: "Personel kartları — kimlik, proje/bölge/şube, pozisyon (asıl/geçici), durum (normal/emekli), SGK ve maaş bilgileri", icon: Users, node: <Personnel /> },
  { key: "fatura", label: "Fiş/Fatura Girişleri", desc: "Gelir/gider faturaları — ödeme planı ve bütçeye otomatik yansır", icon: Receipt, node: <Invoices /> },
  { key: "cek", label: "Verilen Çekler", desc: "Çek kayıtları, Excel ile toplu yükleme ve ödeme takibi", icon: Landmark, node: <Checks /> },
  { key: "kredi", label: "Limit Risk / Banka Kredileri / Kredi Kartları", desc: "Banka limit-risk bilgileri, kredi ve kredi kartı kayıtları, vade/ekstre ve ödeme takibi", icon: Banknote, node: <KrediSection /> },
  { key: "maliyet", label: "Proje Maliyetleri Veri Girişi", desc: "Onaylı müşteri hakedişlerine müşteriye fatura edilmeyen giderleri (yönetim, sözleşme, İSG vb.) girin", icon: Calculator, node: <ProjectCostEntry /> },
];

export default function VeriGirisi() {
  const { user, isAdmin } = useAuth();
  const myCards = user?.assigned_card_ids || [];
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(params.get("bolum") || "");
  const [pendingCosts, setPendingCosts] = useState(0);
  useEffect(() => { setOpen(params.get("bolum") || ""); }, [params]);
  useEffect(() => {
    http.get("/project-costs/pending-count").then((r) => setPendingCosts(r.data?.count || 0)).catch(() => {});
  }, [open]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || e.target.closest("input,textarea,select") || params.get("kayit") || document.querySelector("[data-esc-trap]")) return;
      const don = safePath(params.get("don"));
      if (don) { navigate(don); return; }
      setOpen(""); setParams({}, { replace: true });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [params, setParams, navigate]);
  const toggle = (k) => {
    const v = open === k ? "" : k;
    setOpen(v);
    setParams(v ? { bolum: v } : {}, { replace: true });
  };
  // rol + kredi kartı ataması / limit-risk görünürlüğü
  const sections = useMemo(() => SECTIONS.filter((s) => canSeeSub(user, "/veri-girisi", s.key) && (s.key !== "kredi" || isAdmin || myCards.length > 0 || canLimitRisk(user))), [user, isAdmin, myCards.length]);
  // tek bölüm görebilen rol (ör. taşeron → yalnız Hizmet Veri Girişi): bölüm otomatik açılır
  useEffect(() => {
    if (sections.length === 1 && !params.get("bolum")) { setOpen(sections[0].key); setParams({ bolum: sections[0].key }, { replace: true }); }
  }, [sections, params, setParams]);

  return (
    <div className="space-y-4" data-testid="page-data-hub">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Veri Girişi</h1>
        <p className="text-sm text-slate-500 mt-1">Kayıt girmek istediğiniz bölümün butonuna basın — ilgili veri giriş ekranı aşağıda açılır; bölüm açıkken diğer menüler gizlenir, kapatınca (başlığa tekrar basın ya da ESC) tüm menüler görünür.</p>
      </div>
      {open && sections.some((s) => s.key === open) && (
        <button type="button" onClick={() => toggle(open)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900" data-testid="hub-back-all">
          <ChevronLeft className="w-3.5 h-3.5" /> Tüm veri giriş menüleri
        </button>
      )}
      {sections.filter((s) => !open || s.key === open || !sections.some((x) => x.key === open)).map((s) => {
        const Icon = s.icon;
        const isOpen = open === s.key;
        return (
          <div key={s.key} className={`bg-white border rounded-lg overflow-hidden transition-colors ${isOpen ? "border-amber-400 shadow-sm" : "border-slate-200"}`} data-testid={`hub-sec-${s.key}`}>
            <button onClick={() => toggle(s.key)} data-testid={`hub-btn-${s.key}`}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors">
              <span className={`p-2 rounded-md ${isOpen ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}><Icon className="w-4 h-4" /></span>
              <span className="flex-1">
                <span className="block text-sm font-bold text-slate-900">{s.label}</span>
                <span className="block text-xs text-slate-500">{s.desc}</span>
              </span>
              {s.key === "maliyet" && pendingCosts > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-[11px] font-bold border border-red-200 animate-pulse" data-testid="hub-maliyet-pending">
                  <span className="w-2 h-2 rounded-full bg-red-600 animate-ping" />
                  {pendingCosts} adet maliyet veri girişi bekleyen işlem var
                </span>
              )}
              {isOpen ? <ChevronDown className="w-4 h-4 text-amber-600" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
            {isOpen && (
              <div className="border-t border-amber-200 px-4 py-4" data-testid={`hub-panel-${s.key}`}>
                {s.node}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Limit Risk Bilgileri | Banka Kredileri | Kredi Kartları — yan yana sekme; seçim URL'de `alt=limit|kredi|kart` (kayda-git bağlantıları doğru sekmeyi açar)
function KrediSection() {
  const { user, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const hasCards = (user?.assigned_card_ids || []).length > 0;
  const tabs = [
    ...(canLimitRisk(user) ? [{ key: "limit", label: "Limit Risk Bilgileri", icon: Gauge }] : []),
    ...(isAdmin ? [{ key: "kredi", label: "Banka Kredileri", icon: Banknote }] : []),
    ...(isAdmin || hasCards ? [{ key: "kart", label: "Kredi Kartları", icon: CreditCard }] : []),
  ];
  const alt = params.get("alt");
  const active = tabs.some((t) => t.key === alt) ? alt : tabs[0]?.key;
  const pick = (k) => { const p = new URLSearchParams(params); p.set("alt", k); setParams(p, { replace: true }); };
  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap" role="tablist" data-testid="kredi-tabs">
        {tabs.map((t) => {
          const Icon = t.icon; const on = active === t.key;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => pick(t.key)} data-testid={`kredi-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md border text-sm font-bold transition-colors ${on ? "bg-amber-500 border-amber-500 text-slate-900 shadow-sm" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>
      <div data-testid={`kredi-panel-${active}`}>{active === "limit" ? <LimitRisk /> : active === "kredi" ? <BankLoans /> : <CreditCards />}</div>
    </div>
  );
}

